/**
 * Replay plein ecran : le dessin est rejoue trait par trait en 1,5 s.
 *
 * Le rendu tourne dans une boucle rAF hors React : un setState par frame
 * ferait exactement ce que le CLAUDE.md interdit.
 *
 * Fermeture : swipe vers le bas, Echap, ou le bouton.
 *
 * Le canvas se dimensionne sur la place REELLEMENT disponible dans la scene, et
 * pas sur la fenetre : cale a 90 % du plus petit cote, il poussait les boutons
 * Rejouer et Fermer hors de l'ecran sur un format paysage, et il ne restait
 * qu'Echap pour sortir.
 */
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import type { GalleryItem, RoomSnapshot } from '@retusch/shared';
import { useT } from '../locales/useT.js';
import { playerById } from '../store/roomStore.js';
import { Avatar } from '../ui/Avatar.js';
import { prepareArtwork, type ArtworkLayer, type PreparedArtwork } from './renderArtwork.js';
import { resolveArtwork } from './Gallery.js';
import { downloadBlob } from './shareImage.js';
import { encodeReplayGif } from './encodeGif.js';
import { CloseIcon, DiscordIcon, DownloadIcon } from '../ui/icons.js';
import { usePublicConfig } from '../publicConfig.js';
import { notify } from '../store/roomStore.js';
import { shareGifToDiscord } from './shareToDiscord.js';

const REPLAY_MS = 1500;

/** Nom de fichier sur : le pseudo peut contenir n'importe quoi. */
function slug(nickname: string | undefined): string {
  const clean = (nickname ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
  return clean.length > 0 ? clean.slice(0, 24) : 'retouche';
}
/** Deplacement vertical au-dela duquel le swipe ferme la vue. */
const SWIPE_CLOSE_PX = 90;

export function ReplayOverlay({
  item,
  drawing,
  layers,
  room,
  onClose,
}: {
  item: GalleryItem;
  drawing: ArrayBuffer | null;
  /** Mode collaboratif : l'œuvre entiere, couche par couche. */
  layers?: readonly ArtworkLayer[] | undefined;
  room: RoomSnapshot;
  onClose: () => void;
}): ReactElement {
  const t = useT();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const preparedRef = useRef<PreparedArtwork | null>(null);
  const [ready, setReady] = useState(false);
  const [runId, setRunId] = useState(0);

  const drawer = playerById(room, item.drawerId);
  const guesser = playerById(room, item.guesserId);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const input = drawing === null ? null : resolveArtwork(item, drawing, layers);
    if (canvas === null || input === null) return;

    let alive = true;
    let raf = 0;

    void prepareArtwork(input)
      .then((prepared) => {
        if (!alive) return;
        preparedRef.current = prepared;
        setReady(true);

        const stage = stageRef.current;
        const bounds = stage?.getBoundingClientRect();
        const size = Math.max(
          160,
          Math.min(bounds?.width ?? window.innerWidth, bounds?.height ?? window.innerHeight),
        );
        const pixels = Math.round(size * Math.min(window.devicePixelRatio || 1, 2));
        canvas.style.width = `${Math.round(size)}px`;
        canvas.style.height = `${Math.round(size)}px`;

        const startedAt = performance.now();
        const frame = (now: number): void => {
          if (!alive) return;
          const progress = Math.min((now - startedAt) / REPLAY_MS, 1);
          prepared.setProgress(progress);
          prepared.paint(canvas, { size: pixels, badge: progress >= 1 });
          if (progress < 1) raf = requestAnimationFrame(frame);
        };
        raf = requestAnimationFrame(frame);
      })
      .catch(() => undefined);

    return () => {
      alive = false;
      cancelAnimationFrame(raf);
    };
  }, [item, drawing, layers, runId]);

  const replay = useCallback(() => setRunId((value) => value + 1), []);

  /**
   * Etat des deux telechargements. Un seul a la fois : l'enregistrement filme
   * le canvas, relancer le replay pendant qu'il tourne filmerait deux debuts.
   */
  const [saving, setSaving] = useState<'none' | 'image' | 'gif' | 'discord' | 'done'>('none');

  // Le bouton n'existe QUE si le serveur a un webhook configure. Un bouton qui
  // ne mene nulle part vaut moins que pas de bouton du tout, et cette
  // information ne peut venir que du serveur : lui seul sait ce qu'il sait
  // faire.
  const { discordInvite, shareToDiscord } = usePublicConfig();

  /**
   * Encode le GIF puis le remet au serveur, qui relaie.
   *
   * Le meme encodeur que le telechargement : ce qui part sur Discord est
   * exactement le fichier qu'on aurait enregistre, il n'y a pas deux qualites
   * ni deux tailles a maintenir.
   */
  const shareDiscord = useCallback(() => {
    const prepared = preparedRef.current;
    if (prepared === null) return;

    setSaving('discord');
    void encodeReplayGif(prepared)
      .then((blob) => shareGifToDiscord(blob, room.code, item.slot))
      .then((outcome) => {
        if (outcome === 'ok') {
          notify(
            'gallery.shared',
            'info',
            discordInvite === null
              ? {}
              : { link: { key: 'gallery.openDiscord', href: discordInvite } },
          );
        } else {
          // Une raison par cause : « reessayez » et « trop lourd » ne se
          // corrigent pas de la meme facon, et une bulle qui dit seulement
          // « echec » laisse le joueur recommencer a l'identique.
          notify(
            outcome === 'too-large'
              ? 'gallery.shareTooLarge'
              : outcome === 'busy'
                ? 'gallery.shareBusy'
                : outcome === 'limit'
                  ? 'gallery.shareLimit'
                  : 'gallery.shareFailed',
            'error',
          );
        }
      })
      .catch(() => notify('gallery.shareFailed', 'error'))
      .finally(() => {
        // L'encodage a laisse l'etat partage sur la retouche complete : on
        // rejoue, sinon le canvas de l'ecran reste fige sur la fin.
        setRunId((value) => value + 1);
        setSaving('none');
      });
  }, [discordInvite, item.slot, room.code]);

  const saveImage = useCallback(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;
    setSaving('image');

    // Le replay peut etre en cours : on force la retouche ENTIERE avant de
    // capturer, sinon on telecharge un dessin arrete au milieu d'un trait.
    const prepared = preparedRef.current;
    if (prepared !== null && canvas.width > 0) {
      prepared.setProgress(1);
      prepared.paint(canvas, { size: canvas.width, badge: true });
    }

    canvas.toBlob((blob) => {
      if (blob !== null) downloadBlob(blob, `retusch-${slug(drawer?.nickname)}.png`);
      setSaving('done');
      window.setTimeout(() => setSaving('none'), 2000);
    }, 'image/png');
  }, [drawer?.nickname]);

  const saveGif = useCallback(() => {
    const prepared = preparedRef.current;
    if (prepared === null) return;

    setSaving('gif');
    // Le GIF est rendu dans SON canvas, a sa taille : filmer celui de l'ecran
    // imposerait au fichier la taille de la fenetre et l'etat d'avancement du
    // replay en cours.
    void encodeReplayGif(prepared)
      .then((blob) => {
        downloadBlob(blob, `retusch-${slug(drawer?.nickname)}.gif`);
        setSaving('done');
      })
      .catch(() => setSaving('none'))
      .finally(() => {
        // L'encodage a laisse l'etat partage sur la retouche complete : on
        // rejoue, sinon le canvas de l'ecran reste fige sur la fin.
        setRunId((value) => value + 1);
        window.setTimeout(() => setSaving('none'), 2000);
      });
  }, [drawer?.nickname]);

  // Swipe-down : on ne suit que le geste vertical, sans bouger la vue.
  const startY = useRef<number | null>(null);
  const onPointerDown = useCallback((event: React.PointerEvent) => {
    startY.current = event.clientY;
  }, []);
  const onPointerUp = useCallback(
    (event: React.PointerEvent) => {
      if (startY.current !== null && event.clientY - startY.current > SWIPE_CLOSE_PX) onClose();
      startY.current = null;
    },
    [onClose],
  );

  const found = item.hit && item.elapsedMs !== null;

  return (
    <div
      className="replay"
      role="dialog"
      aria-label={t('gallery.replayTitle')}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
    >
      <div className="replay__handle" aria-hidden="true" />

      {/* Croix en haut a droite, EN PLUS du bouton du bas. C'est la ou l'on va
          la chercher pour fermer une fenetre, et sur un ecran haut le bouton du
          bas peut se trouver a un pouce de distance. Meme role, deux endroits :
          ici c'est justifie, la fenetre occupe tout l'ecran. */}
      <button
        type="button"
        className="replay__close"
        aria-label={t('common.close')}
        title={t('common.close')}
        onClick={onClose}
      >
        <CloseIcon size={20} />
      </button>

      <div className="replay__stage" ref={stageRef}>
        <canvas ref={canvasRef} className="replay__canvas" />
        {!ready && <p className="hint">{t('gallery.loading')}</p>}
      </div>

      <div className="replay__info">
        <span className="replay__party">
          {drawer !== null && <Avatar playerId={drawer.id} avatar={drawer.avatar} colorIndex={drawer.colorIndex} size={26} />}
          {t('reveal.drawnBy', { name: drawer?.nickname ?? '?' })}
        </span>
        <span className="replay__verdict" data-found={found}>
          {found
            ? t('gallery.foundBy', {
                name: guesser?.nickname ?? '?',
                seconds: ((item.elapsedMs ?? 0) / 1000).toFixed(1),
              })
            : guesser === null
              ? t('gallery.noGuesser')
              : t('gallery.missedBy', { name: guesser.nickname })}
        </span>
      </div>

      <div className="replay__actions">
        <button type="button" className="button-ghost" disabled={saving === 'gif'} onClick={replay}>
          {t('gallery.replay')}
        </button>
        {/* Icone de telechargement sur les deux exports : elle dit d'un coup
            d'oeil lesquels de ces boutons produisent un fichier. */}
        <button
          type="button"
          className="button-ghost button-icon"
          disabled={saving !== 'none'}
          onClick={saveImage}
        >
          <DownloadIcon />
          {saving === 'done' ? t('gallery.saved') : t('gallery.download')}
        </button>
        <button
          type="button"
          className="button-ghost button-icon"
          disabled={saving !== 'none'}
          onClick={saveGif}
        >
          <DownloadIcon />
          {saving === 'gif' ? t('share.working') : t('gallery.animation')}
        </button>
        {shareToDiscord && (
          <button
            type="button"
            className="button-ghost button-icon"
            disabled={saving !== 'none'}
            onClick={shareDiscord}
          >
            <DiscordIcon />
            {saving === 'discord' ? t('share.working') : t('gallery.shareDiscord')}
          </button>
        )}
        <button type="button" className="button-primary" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>
    </div>
  );
}
